import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Home, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";

const fmtEnded = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "");

/** Producer view of the homepage "Watch the replay" card: global switch + per-show switches. */
export function useHomepageReplay() {
  const [state, setState] = useState(null);
  useEffect(() => {
    let active = true;
    api.get("/hub/homepage-replay").then((r) => { if (active) setState(r.data); }).catch(() => { if (active) setState({ enabled: false, recent: [] }); });
    return () => { active = false; };
  }, []);

  const setEnabled = useCallback(async (enabled) => {
    try {
      const r = await api.put("/hub/homepage-replay", { enabled });
      setState(r.data);
      toast.success(enabled ? "Replays will show on the homepage for a week after each show" : "The homepage won't show replays");
    } catch (e) { toast.error("Could not update"); }
  }, []);

  const setSessionVisible = useCallback(async (sessionId, visible) => {
    try {
      await api.put(`/hub/sessions/${sessionId}/homepage-replay`, { visible });
      setState((s) => (s ? { ...s, recent: s.recent.map((x) => (x.session_id === sessionId ? { ...x, visible } : x)) } : s));
      toast.success(visible ? "Replay is back on the homepage" : "Replay hidden from the homepage");
      return true;
    } catch (e) { toast.error("Could not update"); return false; }
  }, []);

  return { state, setEnabled, setSessionVisible };
}

export function SessionReplayToggle({ visible, globalEnabled, onChange, testId = "session-replay-toggle" }) {
  return (
    <label className="flex items-center justify-between gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2" data-testid={`${testId}-row`}>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-sm font-medium text-foreground"><Home className="h-3.5 w-3.5 text-primary" />Show this replay on the homepage</span>
        <span className="block text-xs text-muted-foreground" data-testid={`${testId}-hint`}>
          {globalEnabled === false
            ? "Homepage replays are turned off — turn them on from the Podcast Hub page."
            : visible ? "Visitors see it on the homepage for a week after the show." : "Hidden — members can still find it under Videos."}
        </span>
      </span>
      <Switch checked={!!visible} onCheckedChange={onChange} data-testid={testId} aria-label="Show this replay on the homepage" />
    </label>
  );
}

export function HomepageReplayCard() {
  const { state, setEnabled, setSessionVisible } = useHomepageReplay();
  if (!state) return null;
  return (
    <Card className="border-border bg-card" data-testid="homepage-replay-card">
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-sm font-semibold text-foreground"><PlayCircle className="h-4 w-4 text-primary" />Homepage replay</div>
            <p className="text-xs text-muted-foreground">After a show ends, its recording can appear on the public homepage for {state.window_days || 7} days.</p>
          </div>
          <label className="flex shrink-0 items-center gap-2 text-sm text-foreground">
            <span data-testid="homepage-replay-status">{state.enabled ? "On" : "Off"}</span>
            <Switch checked={state.enabled} onCheckedChange={setEnabled} data-testid="homepage-replay-toggle" aria-label="Show replays on the homepage" />
          </label>
        </div>
        {state.recent.length > 0 && (
          <ul className="divide-y divide-border rounded-lg border border-border" data-testid="homepage-replay-recent">
            {state.recent.map((s) => (
              <li key={s.session_id} className="flex items-center justify-between gap-3 px-3 py-2" data-testid={`homepage-replay-session-${s.session_id}`}>
                <div className="min-w-0">
                  <Link to={`/portal/hub/${s.session_id}`} className="block truncate text-sm font-medium text-foreground hover:underline">{s.title}</Link>
                  <p className="text-xs text-muted-foreground">Ended {fmtEnded(s.ended_at)} · {!state.enabled ? "not shown (replays off)" : s.visible ? "showing on the homepage" : "hidden"}</p>
                </div>
                <Switch checked={s.visible} disabled={!state.enabled} onCheckedChange={(v) => setSessionVisible(s.session_id, v)} data-testid={`homepage-replay-session-toggle-${s.session_id}`} aria-label={`Show ${s.title} on the homepage`} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
